<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { onMount } from 'svelte';
  import { push, replace } from 'svelte-spa-router';
  import * as tus from 'tus-js-client';
  import { session } from '../lib/session';
  import { api, ApiError } from '../lib/api';
  import { adminState } from '../lib/admin-state';
  import MarkdownEditor from '../components/MarkdownEditor.svelte';
  import AudioPlayer from '../components/AudioPlayer.svelte';
  import BlockEditor from '../components/BlockEditor.svelte';
  import Lightbox from '../components/Lightbox.svelte';
  import { renderMarkdown } from '../lib/markdown';
  import type { AdminCategoryDTO, Block, MaterialType } from '../lib/types';

  let title = $state('');
  let categoryId = $state('');
  let type = $state<MaterialType>('video');
  let description = $state('');
  let durationSec = $state<number | null>(null);
  let isPublished = $state(false);
  let body = $state<Block[]>([]);
  let categories = $state<AdminCategoryDTO[] | null>(null);
  let saving = $state(false);
  let error = $state<string | null>(null);
  let previewOpen = $state(false);
  // Playback URL is auto-fetched when Bunny upload completes (see $effect
  // below) so the admin sees the player inline immediately. Same state
  // powers both the inline view and the "Перегляд" modal.
  let playbackUrl = $state<string | null>(null);
  let playbackLoading = $state(false);
  let playbackGuid: string | null = null;
  let lightbox = $state<{ images: string[]; index: number } | null>(null);
  function openLightbox(images: string[], index: number): void {
    lightbox = { images, index };
  }

  function openPreview(): void {
    previewOpen = true;
  }

  const TYPE_LABEL: Record<MaterialType, string> = {
    video: '▶️ Відео',
    audio: 'Аудіо',
    text: 'Текст',
  };
  const selectedCategory = $derived(
    categoryId && categories ? categories.find((c) => c.id === categoryId) ?? null : null,
  );

  // Bunny upload state
  let file = $state<File | null>(null);
  let uploadProgress = $state<number | null>(null); // 0..100
  let uploading = $state(false);
  let uploadedGuid = $state<string | null>(null);
  let currentUpload: tus.Upload | null = null;

  // As soon as a Bunny upload finishes we sign a playback URL so the inline
  // player has something to render. Re-fires when admin replaces the file
  // (new GUID) and clears when they remove the file (uploadedGuid → null).
  $effect(() => {
    if (!uploadedGuid) {
      playbackUrl = null;
      playbackGuid = null;
      return;
    }
    if (uploadedGuid === playbackGuid) return;
    playbackGuid = uploadedGuid;
    playbackLoading = true;
    playbackUrl = null;
    api
      .adminBunnyPlayback(uploadedGuid)
      .then(({ iframeUrl }) => {
        // Guard against stale response after another upload started.
        if (playbackGuid === uploadedGuid) playbackUrl = iframeUrl;
      })
      .catch(() => {
        /* fall through to placeholder */
      })
      .finally(() => {
        if (playbackGuid === uploadedGuid) playbackLoading = false;
      });
  });

  // Bunny.net accepts both groups, but to keep semantics clean we split them
  // by media type. Picker accept attr + JS check both use the right slice.
  const VIDEO_EXTS = [
    '4mv', 'amv', 'avi', 'flv', 'm4v', 'mkv', 'mov',
    'mp4', 'mpeg', 'mpg', 'mxf', 'ts', 'vod', 'webm', 'wmv',
  ];
  const AUDIO_EXTS = ['m4p', 'mp3', 'ogg', 'wav'];
  const supportedExts = $derived(type === 'audio' ? AUDIO_EXTS : VIDEO_EXTS);
  // accept attribute. For video we also list video/quicktime explicitly so
  // iOS Photos delivers HEVC .mov files as-is (no slow native transcode in
  // the picker) — Bunny accepts MOV anyway.
  const acceptAttr = $derived(
    type === 'audio'
      ? AUDIO_EXTS.map((e) => '.' + e).join(',') + ',audio/*'
      : VIDEO_EXTS.map((e) => '.' + e).join(',') + ',video/quicktime,video/*',
  );
  // Whether the currently picked file's extension matches the selected type.
  // If not, the dropzone shows the «pick file» state — but we keep the File in
  // state so toggling back restores it without re-picking.
  const fileMatchesType = $derived.by(() => {
    if (!file) return false;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return supportedExts.includes(ext);
  });

  function onFileChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    const f = input.files?.[0] ?? null;
    if (!f) {
      file = null;
      return;
    }
    const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
    if (!supportedExts.includes(ext)) {
      error = `Непідтримуваний формат «.${ext}» для типу ${type === 'audio' ? 'Аудіо' : 'Відео'}. Дивись список нижче.`;
      input.value = '';
      file = null;
      durationSec = null;
      return;
    }
    error = null;
    file = f;
    // Read media duration client-side so we don't need to ask Bunny later.
    try {
      const url = URL.createObjectURL(f);
      const el = (type === 'audio' ? document.createElement('audio') : document.createElement('video')) as HTMLMediaElement;
      el.preload = 'metadata';
      el.src = url;
      el.onloadedmetadata = () => {
        if (Number.isFinite(el.duration) && el.duration > 0) {
          durationSec = Math.round(el.duration);
        }
        URL.revokeObjectURL(url);
      };
    } catch {
      /* not a media file or browser refused metadata — admin can type manually */
    }
    // Kick off the upload immediately so the admin watches progress while
    // they finish filling the rest of the form.
    startUpload(f);
  }

  /** Starts a TUS upload to Bunny. State drives the UI; Save just waits for uploadedGuid. */
  function startUpload(f: File): void {
    cancelUpload();
    uploading = true;
    uploadProgress = 0;
    uploadedGuid = null;
    api
      .adminBunnyUploadCredentials(title.trim() || f.name, categoryId || undefined)
      .then((creds) => {
        currentUpload = new tus.Upload(f, {
          endpoint: 'https://video.bunnycdn.com/tusupload',
          // Chunks keep each PATCH short: when the connection drops, Bunny
          // releases the lock on the upload quickly (otherwise the retry's HEAD
          // gets 423 "upload is currently locked" while the dead PATCH holds it).
          chunkSize: 50 * 1024 * 1024,
          // Longer tail so a lock held by a dropped request has time to expire.
          retryDelays: [0, 3000, 5000, 10000, 20000, 30000, 60000, 60000, 120000],
          headers: {
            AuthorizationSignature: creds.signature,
            AuthorizationExpire: String(creds.expirationTime),
            VideoId: creds.videoGuid,
            LibraryId: creds.libraryId,
          },
          metadata: { filetype: f.type, title: title.trim() || f.name },
          onError(err) {
            uploading = false;
            uploadProgress = null;
            currentUpload = null;
            error = 'Помилка завантаження в Bunny: ' + (err instanceof Error ? err.message : 'невідома');
          },
          onProgress(bytesUploaded, bytesTotal) {
            uploadProgress = Math.round((bytesUploaded / bytesTotal) * 100);
          },
          onSuccess() {
            uploading = false;
            uploadProgress = 100;
            uploadedGuid = creds.videoGuid;
            currentUpload = null;
          },
        });
        currentUpload.start();
      })
      .catch((err) => {
        uploading = false;
        uploadProgress = null;
        error =
          err instanceof ApiError
            ? `Не вдалось отримати credentials (${err.code})`
            : 'Не вдалось почати завантаження';
      });
  }

  function cancelUpload(): void {
    if (currentUpload) {
      try {
        currentUpload.abort(true);
      } catch {
        /* ignore */
      }
      currentUpload = null;
    }
  }

  function removeFile(): void {
    cancelUpload();
    file = null;
    durationSec = null;
    uploadProgress = null;
    uploadedGuid = null;
    error = null;
  }

  onMount(async () => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) {
      replace('/home');
      return;
    }
    // Read-only env (dev/local): creating materials writes to Bunny — not
    // allowed here. Bounce back to the dashboard.
    if (!$session.bunnyWritable) {
      replace('/admin/dashboard');
      return;
    }
    try {
      const r = await api.adminCategories();
      categories = r.items;
      if (!categoryId && categories.length > 0) categoryId = categories[0].id;
    } catch {
      categories = [];
    }
  });

  const canSave = $derived(
    title.trim().length > 0 &&
      categoryId.length > 0 &&
      ((type === 'text' && body.length > 0) ||
        ((type === 'video' || type === 'audio') &&
          file !== null &&
          fileMatchesType &&
          uploadedGuid !== null &&
          !uploading)),
  );

  async function save(): Promise<void> {
    if (!canSave || saving) return;
    saving = true;
    error = null;
    try {
      await api.adminCreateMaterial({
        title: title.trim(),
        categoryId,
        type,
        description: description.trim() || null,
        bunnyVideoId: type === 'video' || type === 'audio' ? uploadedGuid : null,
        durationSec: durationSec ?? null,
        isPublished,
        body: type === 'text' && body.length > 0 ? body : null,
      });
      adminState.activeTab = 'content';
      adminState.contentSubTab = 'materials';
      push('/admin/dashboard');
    } catch (err) {
      error =
        err instanceof ApiError
          ? `Не вдалось зберегти (${err.code})`
          : 'Не вдалось зберегти';
    } finally {
      saving = false;
    }
  }
</script>

<div class="screen">
  <div class="admin-header">
    <a href="#/admin/dashboard" class="back-link">← Назад</a>
    <div class="header-title">Новий матеріал</div>
    <button
      type="button"
      class="header-preview"
      onclick={openPreview}
      aria-label="Перегляд"
      title="Перегляд"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    </button>
  </div>
  <div class="scroll">
    <label class="field-label">Тип</label>
    <div class="type-pills">
      <button
        type="button"
        class="type-pill"
        class:active={type === 'video'}
        onclick={() => (type = 'video')}
      >▶️ Відео</button>
      <button
        type="button"
        class="type-pill"
        class:active={type === 'audio'}
        onclick={() => (type = 'audio')}
      ><Icon name="audio-lines" size="xs" /> Аудіо</button>
      <button
        type="button"
        class="type-pill"
        class:active={type === 'text'}
        onclick={() => (type = 'text')}
      ><Icon name="file-text" size="xs" /> Текст</button>
    </div>

    <label class="field-label">Розділ</label>
    {#if categories === null}
      <div class="placeholder">Завантажуємо розділи…</div>
    {:else}
      <select class="field" bind:value={categoryId}>
        {#each categories as c (c.id)}
          <option value={c.id}>{c.name}</option>
        {/each}
      </select>
    {/if}

    <label class="field-label">Назва</label>
    <input class="field" maxlength="200" placeholder="Назва матеріалу" bind:value={title} />

    <label class="field-label">Опис (необовʼязково)</label>
    <MarkdownEditor bind:value={description} maxlength={2000} placeholder="Короткий опис…" />

    {#if type === 'video' || type === 'audio'}
      <label class="field-label">Файл</label>
      {#if file && fileMatchesType}
        <div class="dropzone has-file">
          <div class="dz-name"><Icon name="cloud-upload" size="xs" /> {file.name}</div>
          <div class="dz-size">{(file.size / (1024 * 1024)).toFixed(1)} MB</div>
          <button
            type="button"
            class="dz-remove"
            onclick={removeFile}
          ><Icon name="x" size="xs" /> {uploading ? 'Скасувати завантаження' : 'Прибрати файл'}</button>
        </div>
      {:else}
        <label class="dropzone">
          <input
            type="file"
            accept={acceptAttr}
            multiple={false}
            onchange={onFileChange}
            disabled={uploading}
            hidden
          />
          <div class="dz-prompt">Обери {type === 'audio' ? 'аудіо' : 'відео'} файл</div>
          <div class="dz-sub">Supported: {supportedExts.join(', ')}</div>
        </label>
      {/if}

      {#if uploadProgress !== null}
        <div class="progress-bar">
          <div class="progress-fill" style="width: {uploadProgress}%"></div>
        </div>
        <div class="progress-label" class:done={uploadedGuid !== null}>
          {#if uploadedGuid !== null}
            <Icon name="check" size="xs" /> Завантажено в Bunny ({uploadProgress}%)
          {:else}
            Завантажуємо в Bunny… {uploadProgress}%
          {/if}
        </div>
      {/if}

      {#if uploadedGuid}
        <label class="field-label">Прев'ю</label>
        {#if playbackUrl}
          {#if type === 'audio'}
            <AudioPlayer src={playbackUrl} trackProgress={false} />
          {:else}
            <div class="player-wrap">
              <iframe
                src={playbackUrl}
                title="Прев'ю"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowfullscreen
              ></iframe>
            </div>
          {/if}
        {:else if playbackLoading}
          <div class="player-placeholder">Готуємо плеєр… (Bunny обробляє файл — це займає 10-60с)</div>
        {/if}
      {/if}
    {/if}

    <!-- Body blocks: required for text materials, optional supplementary
     *   content under the player for video/audio. -->
    <label class="field-label">
      {type === 'text' ? 'Вміст матеріалу' : 'Додатковий вміст (необов\'язково)'}
    </label>
    <BlockEditor blocks={body} onChange={(v) => (body = v)} />

    <label class="checkbox-row">
      <input type="checkbox" bind:checked={isPublished} />
      <span>Опублікувати одразу (інакше зберегти як чернетку)</span>
    </label>

    {#if error}<div class="error">{error}</div>{/if}

    <button
      type="button"
      class="submit"
      disabled={!canSave || saving}
      onclick={save}
    >
      {saving ? 'Зберігаємо…' : 'Створити матеріал'}
    </button>
  </div>

  {#if previewOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" onclick={() => (previewOpen = false)}></div>
    <div class="preview-sheet" role="dialog" aria-modal="true" aria-label="Перегляд">
      <div class="modal-head">
        <div class="modal-title">Перегляд для користувача</div>
        <button type="button" class="modal-close" onclick={() => (previewOpen = false)}><Icon name="x" /></button>
      </div>
      <div class="modal-body preview-page">
        <div class="preview-topbar">
          {selectedCategory
            ? selectedCategory.name
            : '— без розділу —'}
        </div>
        <div class="preview-content">
          <h1 class="title">{title.trim() || '(без назви)'}</h1>
          {#if description.trim()}
            <div class="description md-content">{@html renderMarkdown(description)}</div>
          {/if}
          {#if type === 'audio' && playbackUrl}
            <AudioPlayer src={playbackUrl} trackProgress={false} />
          {:else if type === 'video' && playbackUrl}
            <div class="player-wrap">
              <iframe
                src={playbackUrl}
                title="Перегляд"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowfullscreen
              ></iframe>
            </div>
          {:else if (type === 'video' || type === 'audio') && playbackLoading}
            <div class="placeholder">
              <div class="placeholder-icon"><Icon name={type === 'video' ? 'circle-play' : 'audio-lines'} size="xl" /></div>
              <div class="placeholder-text">Завантажуємо плеєр…</div>
            </div>
          {:else if type === 'video' || type === 'audio'}
            <div class="placeholder">
              <div class="placeholder-icon"><Icon name={type === 'video' ? 'circle-play' : 'audio-lines'} size="xl" /></div>
              <div class="placeholder-text">
                {file
                  ? `Чекаємо завершення завантаження (${uploadProgress ?? 0}%)…`
                  : 'Файл ще не обрано'}
              </div>
            </div>
          {/if}

          {#if body.length === 0 && type === 'text'}
            <div class="placeholder">
              <div class="placeholder-icon"><Icon name="file-text" size="xl" /></div>
              <div class="placeholder-text">Контент не заповнено</div>
            </div>
          {:else}
            {#each body as block (block.id)}
              {#if block.type === 'text'}
                <div class="text-content md-content">{@html renderMarkdown(block.markdown)}</div>
              {:else if block.type === 'image' && block.url}
                <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
                <img
                  class="preview-cover clickable width-{block.width ?? 'full'}"
                  src={block.url}
                  alt=""
                  onclick={() => openLightbox([block.url], 0)}
                />
              {:else if block.type === 'gallery' && block.urls.length > 0}
                <div class="preview-media-group cols-{block.cols && block.urls.length >= block.cols ? block.cols : 2}">
                  {#each block.urls as src, i (src + i)}
                    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
                    <img
                      class="clickable"
                      src={src}
                      alt=""
                      onclick={() => openLightbox(block.urls, i)}
                    />
                  {/each}
                </div>
              {/if}
            {/each}
          {/if}
          <div class="preview-state-row">
            <span class="preview-state-pill" class:pub={isPublished}>
              {#if isPublished}<Icon name="check" size="xs" /> Опубліковано{:else}◌ Чернетка{/if}
            </span>
            <span class="preview-state-pill neutral">{TYPE_LABEL[type]}</span>
            {#if !categoryId}
              <span class="preview-state-pill warn"><Icon name="triangle-alert" size="xs" /> Без розділу</span>
            {/if}
          </div>
        </div>
      </div>
    </div>
  {/if}

  {#if lightbox}
    <Lightbox
      images={lightbox.images}
      initialIndex={lightbox.index}
      onClose={() => (lightbox = null)}
    />
  {/if}
</div>

<style>
  .screen { display: flex; flex-direction: column; flex: 1; min-height: 0; background: var(--bg); }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 14px 18px;
    color: var(--text-light);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .back-link { font-size: 12px; color: rgba(251, 246, 236,0.9); text-decoration: none; }
  .header-title { flex: 1; text-align: center; font-size: 14px; font-weight: 600; }

  .scroll { flex: 1; overflow-y: auto; padding: 18px 18px 100px; }
  .field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    margin: 12px 0 6px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .field {
    width: 100%;
    border: 1px solid var(--border2);
    border-radius: 10px;
    padding: 11px 13px;
    font-size: 13px;
    color: var(--text);
    background: var(--bg);
    font-family: inherit;
    outline: none;
    margin-bottom: 4px;
  }
  .field:focus { border-color: var(--outline); }
  .field.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
  textarea.field { resize: vertical; min-height: 80px; }

  .type-pills { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 4px; }
  .type-pill {
    border: 1px solid var(--border2);
    background: var(--bg);
    color: var(--text);
    padding: 7px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
  }
  .type-pill.active {
    background: var(--pu);
    color: var(--text-light);
    border-color: var(--pu);
  }

  .placeholder {
    padding: 12px;
    text-align: center;
    color: var(--text-muted);
    font-size: 12px;
    border: 1px dashed var(--border2);
    border-radius: 10px;
    background: var(--bg2);
  }
  .checkbox-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 16px 0 8px;
    font-size: 13px;
    cursor: pointer;
  }
  .checkbox-row input[type='checkbox'] {
    width: 16px;
    height: 16px;
    accent-color: var(--pu);
  }
  .error {
    margin: 12px 0;
    padding: 10px 14px;
    background: var(--danger-bg);
    color: var(--status-inactive);
    border: 1px solid var(--danger-border);
    border-radius: 10px;
    font-size: 12px;
  }
  .submit {
    width: 100%;
    margin-top: 18px;
    padding: 14px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: opacity 0.15s;
  }
  .submit:disabled { opacity: 0.5; cursor: not-allowed; }

  /* === Bunny upload widgets === */
  .dropzone {
    display: block;
    width: 100%;
    padding: 22px 16px;
    border: 2px dashed var(--border2);
    border-radius: 12px;
    background: var(--bg2);
    text-align: center;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
  }
  .dropzone:hover { border-color: var(--pu); }
  .dropzone.has-file {
    border-style: solid;
    border-color: var(--pu);
    background: var(--pu-light);
  }
  .dz-prompt {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    margin-bottom: 4px;
  }
  .dz-sub {
    font-size: 11px;
    color: var(--text-muted);
  }
  .dz-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--accent-text);
    word-break: break-all;
  }
  .dz-size {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 2px;
  }
  .dz-remove {
    margin-top: 10px;
    border: 1px solid var(--border2);
    background: var(--bg);
    color: var(--text-muted);
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
  }
  .dz-remove:hover {
    color: var(--status-inactive);
    border-color: var(--danger-border);
  }
  .dz-remove:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .progress-bar {
    margin-top: 10px;
    height: 6px;
    background: var(--pu-light);
    border-radius: 3px;
    overflow: hidden;
  }
  .progress-fill {
    height: 100%;
    background: var(--pu2);
    transition: width 0.2s var(--ease);
  }
  .progress-label {
    margin-top: 6px;
    font-size: 11px;
    color: var(--text-muted);
    text-align: center;
  }
  .progress-label.done {
    color: var(--status-active);
    font-weight: 600;
  }
  /* Inline media player rendered right under the upload progress so admin
   * sees what they uploaded without opening the preview modal. */
  .player-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    background: black;
    margin-bottom: 14px;
  }
  .player-wrap iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
  .player-placeholder {
    padding: 30px 16px;
    text-align: center;
    background: var(--bg2);
    border: 1px dashed var(--border2);
    border-radius: 12px;
    color: var(--text-muted);
    font-size: 13px;
    margin-bottom: 14px;
    line-height: 1.5;
  }

  /* Preview button in header (eye icon) */
  .header-preview {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: rgba(251, 246, 236, 0.15);
    border: 1px solid rgba(251, 246, 236, 0.25);
    color: var(--text-light);
    cursor: pointer;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .header-preview:hover { background: rgba(251, 246, 236, 0.25); }
  .header-preview svg { color: var(--text-light); display: block; }

  /* Preview modal — mirrors AdminMaterialEditPage's preview so admin sees
     the same layout that the edit view shows. */
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 1000;
  }
  .preview-sheet {
    position: fixed;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: min(100%, 480px);
    max-height: 92vh;
    background: var(--bg);
    border-radius: 16px 16px 0 0;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    box-shadow: 0 -16px 40px rgba(0, 0, 0, 0.25);
    animation: sheet-up 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes sheet-up {
    from { transform: translate(-50%, 100%); }
    to { transform: translate(-50%, 0); }
  }
  .modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px 12px;
    border-bottom: 0.5px solid var(--border);
    flex-shrink: 0;
  }
  .modal-title { font-size: 16px; font-weight: 700; color: var(--text); }
  .modal-close {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--bg2);
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 14px;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .modal-body {
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }
  .preview-page { padding: 0; }
  .preview-topbar {
    padding: 12px 18px;
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    color: var(--text-light);
    font-size: 13px;
    font-weight: 600;
    text-align: center;
  }
  .preview-content { padding: 18px 18px 100px; }
  .preview-cover {
    width: 100%;
    height: auto;
    border-radius: 12px;
    margin-bottom: 14px;
    display: block;
  }
  .preview-cover.width-medium {
    width: 70%;
    margin-left: auto;
    margin-right: auto;
  }
  .preview-cover.width-small {
    width: 45%;
    margin-left: auto;
    margin-right: auto;
  }
  .preview-media-group {
    display: grid;
    gap: 8px;
    margin-top: 14px;
    margin-bottom: 18px;
  }
  .preview-media-group.cols-2 { grid-template-columns: repeat(2, 1fr); }
  .preview-media-group.cols-3 { grid-template-columns: repeat(3, 1fr); }
  .preview-media-group.cols-4 { grid-template-columns: repeat(4, 1fr); }
  .preview-content :global(.clickable) {
    cursor: zoom-in;
  }
  .preview-media-group img {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 10px;
    display: block;
  }
  .preview-content .title {
    font-size: 22px;
    font-weight: 700;
    color: var(--text);
    line-height: 1.3;
    margin: 0 0 16px;
  }
  .preview-content .description {
    font-size: 13px;
    line-height: 1.6;
    color: var(--text-muted);
    margin-bottom: 14px;
  }
  .preview-content .text-content {
    font-size: 14px;
    line-height: 1.7;
    color: var(--text);
    margin-bottom: 18px;
    padding: 16px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
  }
  .preview-content .player-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    background: black;
    margin-bottom: 18px;
  }
  .preview-content .player-wrap iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
  .preview-content .placeholder {
    padding: 40px 20px;
    text-align: center;
    background: var(--bg2);
    border: 1px dashed var(--border2);
    border-radius: 12px;
    margin-bottom: 18px;
  }
  .preview-content .placeholder-icon { font-size: 36px; margin-bottom: 8px; }
  .preview-content .placeholder-text { font-size: 12px; color: var(--text-muted); }
  .preview-state-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .preview-state-pill {
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--bg2);
    color: var(--text-muted);
    border: 1px solid var(--border);
  }
  .preview-state-pill.pub {
    background: var(--green-light);
    color: var(--green);
    border-color: var(--green-light);
  }
  .preview-state-pill.neutral {
    background: var(--pu-light);
    color: var(--accent-text);
    border-color: transparent;
  }
  .preview-state-pill.warn {
    background: var(--warn-bg);
    color: var(--status-expiring);
    border-color: var(--warn-border);
  }
</style>
